import React, { useState } from 'react';
import { Route, Workflow, RefreshCcw, Eye, MapPin, FileSearch, FileCheck, ArrowRight, Check } from 'lucide-react';
import { useStepper } from './useStepper';
import { Controls, Tabs } from './OpsSections';
import { scrollToId } from './homeData';

const CAPS = [
  { icon: Route, t: 'Location-Based Lead Routing', d: 'Direct inquiries to the appropriate team using agreed service-area and location rules.' },
  { icon: Workflow, t: 'Consistent Follow-Up', d: 'Coordinate approved messages, booking steps, reminders, and escalation across branches.' },
  { icon: RefreshCcw, t: 'Lead & Customer Reactivation', d: 'Configure location-specific outreach to eligible past prospects and customers within the agreed scope.' },
  { icon: Eye, t: 'Central Visibility', d: 'Track lead ownership, follow-up status, appointment requests, and location-level activity.' },
];
const LOCS = [{ id: 'north', label: 'North' }, { id: 'south', label: 'South' }, { id: 'east', label: 'East' }];
const TEAMS = { north: 'North Office Team', south: 'South Front Desk', east: 'East Service Desk' };
const SCN = [
  { id: 'new', label: 'New Service Inquiry', industry: 'Home Services', service: 'AC Repair Visit', channel: 'Website Form', contact: 'New Inquiry', follow: 'Approved follow-up sent with booking link', status: 'Appointment Requested' },
  { id: 'after', label: 'After-Hours Request', industry: 'Dental Group', service: 'New Patient Exam', channel: 'Missed Call · After Hours', contact: 'New Inquiry', follow: 'Approved after-hours text-back sent', status: 'Replied · Awaiting Front Desk' },
  { id: 'past', label: 'Past Lead Reactivation', industry: 'Med Spa Locations', service: 'Treatment Consultation', channel: 'Eligible Past Lead', contact: 'Unconverted Prospect', follow: 'Approved reactivation message sent', status: 'Reactivated · Appointment Requested' },
];
const FLOW = ['Inquiry Received', 'Routing Rule Applied', 'Location Assigned', 'Follow-Up Started', 'Team Notified'];
const ROWS = [
  ['north', 'Home Services', 'Furnace Tune-Up', 'Replied', 'Confirm Time', 'Requested'],
  ['south', 'Dental Group', 'Cleaning', 'Rebook Requested', 'Offer Openings', 'Requested'],
  ['east', 'Auto Service', 'Brake Inspection', 'Confirmed', 'Send Reminder', 'Confirmed By Advisor'],
  ['north', 'Med Spa', 'Consultation', 'Reactivated', 'Coordinator Call', 'Requested'],
  ['east', 'Home Services', 'Drain Service', 'New Inquiry', 'Send Follow-Up', 'None Yet'],
  ['south', 'Auto Service', 'Oil Change', 'Returning Customer', 'Confirm Visit', 'Requested'],
];

const InquiryCard = ({ sc, loc }) => (
  <div className="yv-ml-card yv-keep-case" data-testid="ml-inquiry-card">
    <span className="yv-mono">Sample Inquiry</span>
    <dl>
      <div><dt>Industry</dt><dd>{sc.industry}</dd></div>
      <div><dt>Service</dt><dd>{sc.service}</dd></div>
      <div><dt>Channel</dt><dd>{sc.channel}</dd></div>
      <div><dt>Service Area</dt><dd>{LOCS.find((l) => l.id === loc).label} Service Area</dd></div>
      <div><dt>Contact</dt><dd>{sc.contact}</dd></div>
    </dl>
  </div>
);

const Dashboard = () => {
  const [f, setF] = useState('all');
  const rows = ROWS.filter((r) => f === 'all' || r[0] === f);
  return (
    <div className="yv-ml-dash" data-testid="ml-dashboard">
      <div className="yv-ml-dash-head"><b>Operations Preview</b><Tabs items={[{ id: 'all', label: 'All Locations' }, ...LOCS]} sel={f} onPick={setF} id="ml-dash" label="Dashboard filter" /></div>
      <div className="yv-ml-table-wrap">
        <table className="yv-ml-table">
          <thead><tr><th>Location</th><th>Responsible Team</th><th>Request</th><th>Lead Status</th><th>Next Follow-Up</th><th>Appointment</th></tr></thead>
          <tbody>{rows.map((r, k) => (
            <tr key={k} data-testid="ml-dash-row"><td>{LOCS.find((l) => l.id === r[0]).label}</td><td>{TEAMS[r[0]]}</td><td>{r[1]} · {r[2]}</td><td><span className="yv-st yv-st-blue">{r[3]}</span></td><td>{r[4]}</td><td><span className={`yv-st ${r[5].startsWith('Confirmed') ? 'yv-st-green' : r[5] === 'Requested' ? 'yv-st-amber' : 'yv-st-slate'}`}>{r[5]}</span></td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
};

const pickMulti = () => { window.dispatchEvent(new CustomEvent('yv:package', { detail: 'multi-location' })); scrollToId('contact'); };

export const MultiLocation = () => {
  const [scn, setScn] = useState('new');
  const [loc, setLoc] = useState('north');
  const sc = SCN.find((x) => x.id === scn);
  const s = useStepper(5, `${scn}-${loc}`, { delay: 1100 });
  const L = LOCS.find((l) => l.id === loc).label;
  const watch = () => { document.getElementById('ml-demo')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); s.replay(); };
  return (
    <section id="routing" className="yv-section yv-dark yv-grid-dark" data-testid="routing-section">
      <div className="yv-container">
        <div className="yv-section-head yv-split-head">
          <div>
            <p className="yv-eyebrow">Multi-Location Operations</p>
            <h2 className="yv-h2">More Locations. <span className="yv-blue-ink yv-block">One Coordinated Operation.</span></h2>
          </div>
          <div>
            <p className="yv-lede">Connect Lead Routing, Customer Follow-Up, Booking Workflows, And Team Visibility Across Your Locations—With Local Execution And A Clear View Of The Whole Business.</p>
            <div className="yv-hero-actions">
              <button onClick={pickMulti} className="yv-btn yv-btn-primary" data-testid="ml-map-btn">Map My Multi-Location System <ArrowRight className="w-4 h-4" aria-hidden="true" /></button>
              <button onClick={watch} className="yv-btn yv-btn-ghost" data-testid="ml-watch-btn">Watch A Lead Get Routed</button>
            </div>
          </div>
        </div>
        <ul className="yv-ml-caps">{CAPS.map((c) => { const I = c.icon; return <li key={c.t}><I className="w-5 h-5" aria-hidden="true" /><b>{c.t}</b><span>{c.d}</span></li>; })}</ul>

        <div className="yv-om" id="ml-demo" data-testid="ml-demo">
          <div className="yv-om-top"><span className="yv-tag yv-tag-dark">Illustrative Operations Preview · Sample Data</span><Controls s={s} label="routing" /></div>
          <div className="yv-ml-sel">
            <Tabs items={SCN} sel={scn} onPick={setScn} id="ml-scenario" label="Routing scenario" />
            <Tabs items={LOCS} sel={loc} onPick={setLoc} id="ml-location" label="Service area location" />
          </div>
          <div className="yv-ml-grid">
            <InquiryCard sc={sc} loc={loc} />
            <ol className="yv-ml-flow" aria-live="polite" data-testid="ml-flow">
              {FLOW.map((f, k) => (
                <li key={f} className={k < s.i ? 'done' : k === s.i ? 'on' : ''} data-testid={`ml-flow-${k}`}>
                  <span className="yv-ml-flow-dot">{k < s.i ? <Check className="w-3 h-3" aria-hidden="true" /> : k + 1}</span>
                  <div><b>{f}</b>{k <= s.i && <span className="yv-keep-case">{[sc.channel, `Service Area Matches ${L} Location`, `Assigned To ${TEAMS[loc]}`, sc.follow, `${TEAMS[loc]} notified · Status: ${sc.status}`][k]}</span>}</div>
                </li>
              ))}
            </ol>
            <ul className="yv-ml-locs">
              {LOCS.map((l) => (
                <li key={l.id} className={l.id === loc && s.i >= 2 ? 'on' : ''} data-testid={`ml-loc-${l.id}`}><MapPin className="w-4 h-4" aria-hidden="true" /><b>{l.label} Location</b><span>{l.id === loc && s.i >= 2 ? 'Lead Assigned' : 'Standing By'}</span></li>
              ))}
            </ul>
          </div>
        </div>
        <Dashboard />

        <div className="yv-ml-assess" data-testid="ml-assessment">
          <div>
            <h3 className="yv-h3">Build The System Around Your Locations.</h3>
            <p>Start With An Assessment Of Your Locations, Current Tools, And Follow-Up Process. We’ll Define The Routing Rules, Workflows, Reporting, And Rollout Your Operation Needs.</p>
            <div className="yv-hero-actions">
              <button onClick={pickMulti} className="yv-btn yv-btn-primary" data-testid="ml-assess-btn">Request My Operations Assessment <ArrowRight className="w-4 h-4" aria-hidden="true" /></button>
              <button onClick={() => scrollToId('plan-multi-location')} className="yv-link yv-ml-price-link" data-testid="ml-pricing-link">See The Multi-Location Operations System</button>
            </div>
          </div>
          <ul>{[[MapPin, 'Location & Workflow Map'], [FileSearch, 'Integration & Rollout Scope'], [FileCheck, 'Confirmed Implementation Proposal']].map(([I, t]) => <li key={t}><I className="w-5 h-5" aria-hidden="true" />{t}</li>)}</ul>
        </div>
      </div>
    </section>
  );
};
